import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import StatusBadge from "@/components/app/StatusBadge";
import PartnerCreditCard from "@/components/app/PartnerCreditCard";
import PayPalPayment from "@/components/app/PayPalPayment";
import { toast } from "sonner";
import { paymentMethodLabel } from "@/lib/orders";
import { Input } from "@/components/ui/input";
import { Wallet, Landmark, CreditCard, Sparkles, TicketPercent, X } from "lucide-react";

function PromoCodeBox({ pkg, promo, setPromo, disabled }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/promo-codes/validate", { code, package_id: pkg.id });
      setPromo(data); toast.success(`Codice ${data.promo_code} applicato`);
    } catch (e) { setPromo(null); toast.error(e.response?.data?.detail || "Codice non valido"); }
    finally { setBusy(false); }
  };
  if (promo) return (
    <div className="flex items-center justify-between rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="promo-applied">
      <span className="text-amber-900"><TicketPercent className="w-4 h-4 inline mr-1" />Codice <strong className="font-mono">{promo.promo_code}</strong> applicato</span>
      {!disabled && <Button variant="ghost" size="icon" onClick={() => { setPromo(null); setCode(""); }} data-testid="promo-remove"><X className="w-4 h-4" /></Button>}
    </div>
  );
  if (disabled) return null;
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase font-bold text-slate-500 tracking-widest">Hai un codice promo?</div>
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && apply()} placeholder="Inserisci codice" className="font-mono" data-testid="promo-input" />
        <Button variant="outline" onClick={apply} disabled={busy || !code.trim()} data-testid="promo-apply">Applica</Button>
      </div>
    </div>
  );
}

export default function PartnerRecharge() {
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [credit, setCredit] = useState(null);
  const [buying, setBuying] = useState(null); // { pkg }
  const [recharge, setRecharge] = useState(null); // created recharge order
  const [bankInfo, setBankInfo] = useState(null); // { causale, iban, holder, amount }
  const [method, setMethod] = useState(null); // 'paypal' | 'bonifico'
  const [promo, setPromo] = useState(null);
  const price = recharge || promo || buying;

  const loadAll = useCallback(async () => {
    const [p, h, c] = await Promise.all([
      api.get("/recharge-packages"),
      api.get("/recharges"),
      api.get("/partners/me/credit").catch(() => ({ data: null })),
    ]);
    setPackages(p.data); setHistory(h.data); setCredit(c.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const startPurchase = (pkg) => { setBuying(pkg); setRecharge(null); setBankInfo(null); setMethod(null); setPromo(null); };
  const closePurchase = () => { setBuying(null); setRecharge(null); setBankInfo(null); setMethod(null); setPromo(null); };

  const choose = async (m) => {
    setMethod(m);
    try {
      const { data } = await api.post("/recharges", { package_id: buying.id, payment_method: m, promo_code: promo?.promo_code || null });
      setRecharge(data);
      if (m === "bonifico") {
        const { data: bank } = await api.post(`/recharges/${data.id}/bank-transfer/request`);
        setBankInfo(bank);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione ricarica"); setMethod(null); }
  };

  const paypalCapture = async (paypalOrderId) => {
    try {
      await api.post(`/recharges/${recharge.id}/paypal/capture`, { paypal_order_id: paypalOrderId });
      toast.success("Ricarica completata · credito accreditato");
      closePurchase(); await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore capture PayPal"); }
  };

  // adapter: PayPalPayment component expects an order-like object → fornisci total e id
  const paypalOrderLike = recharge ? {
    id: recharge.id,
    order_number: recharge.order_number,
    total: recharge.total,
    _recharge: true,
  } : null;

  return (
    <div className="space-y-6" data-testid="partner-recharge">
      <div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Ricarica Credito Partner</h1>
        <p className="text-slate-500 mt-1">Acquista pacchetti prepagati per i corsi E-learning e sfrutta i bonus dedicati.</p>
      </div>

      {credit && <PartnerCreditCard credit={credit} compact="minimal" />}

      <div>
        <h2 className="font-display text-xl font-bold mb-3">Pacchetti disponibili</h2>
        {packages.length === 0 ? (
          <Card className="border-slate-200"><CardContent className="p-6 text-slate-500">Nessun pacchetto disponibile per il tuo profilo.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map((p) => (
              <Card key={p.id} className="border-slate-200 card-hover" data-testid={`package-card-${p.id}`}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display text-xl font-extrabold text-slate-900">{p.name}</div>
                      {p.description && <div className="text-sm text-slate-500 mt-0.5">{p.description}</div>}
                    </div>
                    {p.bonus > 0 && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Sparkles className="w-3 h-3 mr-1" />Bonus +{eur(p.bonus)}</Badge>}
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Imponibile</span><span className="font-semibold">{eur(p.net_amount)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">IVA {p.vat_rate}%</span><span>{eur(p.vat_amount)}</span></div>
                    <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1 mt-1"><span>Totale</span><span data-testid={`package-total-${p.id}`}>{eur(p.total)}</span></div>
                  </div>
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-emerald-800">
                    <div className="text-xs uppercase tracking-widest font-bold">Ricevi credito</div>
                    <div className="font-display font-extrabold text-2xl" data-testid={`package-credit-${p.id}`}>{eur(p.credit_amount)}</div>
                  </div>
                  <Button onClick={() => startPurchase(p)} className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-white" data-testid={`buy-package-${p.id}`}>
                    <Wallet className="w-4 h-4 mr-2" /> Acquista
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-display text-xl font-bold mb-3">Storico ricariche</h2>
        <Card className="border-slate-200"><CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead><TableHead>Numero</TableHead><TableHead>Pacchetto</TableHead>
              <TableHead>Totale</TableHead><TableHead>Credito</TableHead><TableHead>Metodo</TableHead><TableHead>Stato</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.map((r) => (
                <TableRow key={r.id} data-testid={`history-row-${r.order_number}`}>
                  <TableCell>{dateIt(r.created_at)}</TableCell>
                  <TableCell className="font-semibold">{r.order_number}</TableCell>
                  <TableCell>{r.package_name}
                    {r.promo_code && <div className="text-[11px] font-mono text-amber-700">{r.promo_code} · -{eur(r.discount_amount)}</div>}
                  </TableCell>
                  <TableCell>{eur(r.total)}</TableCell>
                  <TableCell className="text-emerald-700 font-semibold">{eur(r.credit_amount)}</TableCell>
                  <TableCell className="text-xs">{paymentMethodLabel(r.payment_method)}</TableCell>
                  <TableCell><StatusBadge status={r.payment_status} /></TableCell>
                </TableRow>
              ))}
              {history.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Nessuna ricarica eseguita.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      </div>

      <Dialog open={!!buying} onOpenChange={(v) => { if (!v) closePurchase(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="recharge-dialog">
          <DialogHeader><DialogTitle>Acquista pacchetto · {buying?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
              {price?.promo_code && <>
                <div className="flex justify-between text-slate-500"><span>Imponibile pacchetto</span><span className="line-through">{eur(price.original_net_amount)}</span></div>
                <div className="flex justify-between text-amber-700 font-semibold"><span>Sconto {price.promo_code}{price.discount_type === "percent" ? ` (${price.discount_value}%)` : ""}</span><span data-testid="promo-discount-amount">-{eur(price.discount_amount)}</span></div>
              </>}
              <div className="flex justify-between"><span>Imponibile</span><span className="font-semibold" data-testid="recharge-net">{eur(price?.net_amount || 0)}</span></div>
              <div className="flex justify-between"><span>IVA {price?.vat_rate}%</span><span>{eur(price?.vat_amount || 0)}</span></div>
              <div className="flex justify-between font-bold border-t border-slate-200 pt-1 mt-1"><span>Totale da pagare</span><span data-testid="recharge-total">{eur(price?.total || 0)}</span></div>
              <div className="flex justify-between text-emerald-700 font-semibold mt-1"><span>Credito accreditato</span><span data-testid="recharge-credit">{eur(price?.credit_amount || 0)}</span></div>
            </div>
            {buying && <PromoCodeBox pkg={buying} promo={promo} setPromo={setPromo} disabled={!!recharge} />}
            {!recharge && (
              <div className="space-y-2">
                <div className="text-xs uppercase font-bold text-slate-500 tracking-widest">Scegli il metodo di pagamento</div>
                <Button onClick={() => choose("paypal")} disabled={method === "paypal"} className="w-full h-11 bg-[#0070BA] hover:bg-[#005ea6] text-white" data-testid="pay-paypal-btn">
                  <CreditCard className="w-4 h-4 mr-2" /> PayPal / Carta
                </Button>
                <Button onClick={() => choose("bonifico")} disabled={method === "bonifico"} variant="outline" className="w-full h-11" data-testid="pay-bonifico-btn">
                  <Landmark className="w-4 h-4 mr-2" /> Bonifico Bancario
                </Button>
              </div>
            )}
            {recharge && method === "paypal" && (
              <div>
                <div className="text-xs uppercase font-bold text-slate-500 tracking-widest mb-2">Completa il pagamento PayPal</div>
                <PayPalPayment order={paypalOrderLike} onPaid={paypalCapture}
                  createPath={`/recharges/${recharge.id}/paypal/create`} capturePath={`/recharges/${recharge.id}/paypal/capture`} />
              </div>
            )}
            {recharge && method === "bonifico" && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-1" data-testid="bonifico-instructions">
                <div className="font-bold">Istruzioni bonifico</div>
                <div>Intestatario: <strong data-testid="bonifico-holder">{bankInfo?.holder || "—"}</strong></div>
                <div>IBAN: <strong data-testid="bonifico-iban">{bankInfo?.iban || "—"}</strong></div>
                <div>Causale: <strong data-testid="bonifico-causale">{bankInfo?.causale || recharge.order_number}</strong></div>
                <div>Importo: <strong>{eur(bankInfo?.amount ?? recharge.total)}</strong></div>
                <div className="text-xs text-amber-800 pt-1">Il credito verrà accreditato dopo la conferma del pagamento da parte dell'amministratore.</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePurchase} data-testid="recharge-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
